import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import TrackerPanel from "./tracker/TrackerPanel";

const LS_KEY = "global_split_view_layout:v1";
// const MIN_RIGHT_WIDTH = 320;
const MIN_RIGHT_WIDTH = 520;
// const MAX_RIGHT_WIDTH = 920;
const MAX_RIGHT_WIDTH = 1920;
const DEFAULT_RIGHT_WIDTH = 420;
const RAIL_WIDTH = 48;
const OPEN_DIVIDER_WIDTH = 8;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function safeParse(json, fallback) {
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

function SettingsIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M19.4 15a7.9 7.9 0 0 0 .1-1l2-1.5-2-3.5-2.4.6a7.5 7.5 0 0 0-1.7-1L15 6l-4-.5-.4 2.4a7.5 7.5 0 0 0-1.7 1L6.5 8.9l-2 3.5L6.5 14a7.9 7.9 0 0 0 .1 1l-2 1.5 2 3.5 2.4-.6a7.5 7.5 0 0 0 1.7 1L9 22l4 .5.4-2.4a7.5 7.5 0 0 0 1.7-1l2.4.6 2-3.5-2-1.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const GlobalSplitViewLayout = ({ children }) => {
  const location = useLocation();
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const defaults = useMemo(
    () => ({
      isRightOpen: true,
      rightWidth: DEFAULT_RIGHT_WIDTH,
    }),
    []
  );

  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  const [ui, setUi] = useState(() => {
    const raw = localStorage.getItem(LS_KEY);
    const saved = raw ? safeParse(raw, defaults) : defaults;
    return {
      isRightOpen:
        typeof saved.isRightOpen === "boolean" ? saved.isRightOpen : true,
      rightWidth:
        typeof saved.rightWidth === "number"
          ? clamp(saved.rightWidth, MIN_RIGHT_WIDTH, MAX_RIGHT_WIDTH)
          : DEFAULT_RIGHT_WIDTH,
    };
  });

  const { isRightOpen, rightWidth } = ui;
  const hideRightPanel = location.pathname.startsWith("/tracker");

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ isRightOpen, rightWidth }));
  }, [isRightOpen, rightWidth]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!draggingRef.current || !isDesktop) {
        return;
      }

      const container = containerRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      let nextWidth = rect.width - pointerX;
      nextWidth = clamp(nextWidth, MIN_RIGHT_WIDTH, MAX_RIGHT_WIDTH);
      setUi((prev) => ({ ...prev, rightWidth: nextWidth }));
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDesktop]);

  const startResize = () => {
    if (!isDesktop || !isRightOpen) {
      return;
    }
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const openRightPanel = () => setUi((prev) => ({ ...prev, isRightOpen: true }));
  const closeRightPanel = () => setUi((prev) => ({ ...prev, isRightOpen: false }));

  const gridTemplateColumns = isDesktop
    ? hideRightPanel
      ? "minmax(0,1fr)"
      : isRightOpen
      ? `minmax(0,1fr) ${OPEN_DIVIDER_WIDTH}px ${rightWidth}px`
      : `minmax(0,1fr) 0px ${RAIL_WIDTH}px`
    : "minmax(0,1fr)";
  const trackerPanelOffset =
    isDesktop && !hideRightPanel
      ? isRightOpen
        ? `${rightWidth + OPEN_DIVIDER_WIDTH}px`
        : `${RAIL_WIDTH}px`
      : "0px";

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--tracker-panel-offset",
      trackerPanelOffset
    );

    return () => {
      document.documentElement.style.setProperty("--tracker-panel-offset", "0px");
    };
  }, [trackerPanelOffset]);

  return (
    <div
      ref={containerRef}
      className="grid w-full min-w-0"
      style={{
        gridTemplateColumns,
        "--tracker-panel-offset": trackerPanelOffset,
        transition: "grid-template-columns 260ms cubic-bezier(0.2, 0.8, 0.2, 1)",
      }}
    >
      <div className="min-w-0">{children}</div>

      {isDesktop && !hideRightPanel ? (
        <div
          onMouseDown={startResize}
          title={isRightOpen ? "Drag to resize" : ""}
          className="bg-slate-200/80"
          style={{
            cursor: isRightOpen ? "col-resize" : "default",
            borderLeft: isRightOpen ? "1px solid #e2e8f0" : "none",
            borderRight: isRightOpen ? "1px solid #e2e8f0" : "none",
            opacity: isRightOpen ? 1 : 0,
            pointerEvents: isRightOpen ? "auto" : "none",
            transition: "opacity 180ms ease",
          }}
        />
      ) : null}

      {isDesktop && !hideRightPanel ? (
        <aside className="relative z-[60] border-l border-slate-200 bg-white">
          <div
            className="absolute inset-0 p-4"
            style={{
              opacity: isRightOpen ? 1 : 0,
              transform: isRightOpen ? "translateX(0)" : "translateX(12px)",
              pointerEvents: isRightOpen ? "auto" : "none",
              transition: "opacity 200ms ease, transform 200ms ease",
            }}
          >
            <div className="flex h-full min-h-[calc(100vh-8.5rem)] flex-col rounded-2xl bg-gradient-to-b from-white to-slate-50 p-4 ring-1 ring-slate-200">
              <div className="mb-4 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-600">
                  Tracker Panel
                </h2>
                <button
                  type="button"
                  onClick={closeRightPanel}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
                >
                  Close
                </button>
              </div>

              <TrackerPanel mode="split" />
            </div>
          </div>

          <div
            className="absolute inset-0 flex items-start justify-center pt-4"
            style={{
              opacity: isRightOpen ? 0 : 1,
              transform: isRightOpen ? "translateX(-8px)" : "translateX(0)",
              pointerEvents: isRightOpen ? "none" : "auto",
              transition: "opacity 200ms ease, transform 200ms ease",
            }}
          >
            <button
              type="button"
              onClick={openRightPanel}
              title="Open tracker panel"
              className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:scale-105 hover:bg-slate-50"
            >
              <SettingsIcon className="h-5 w-5" />
            </button>
          </div>
        </aside>
      ) : null}
    </div>
  );
};

export default GlobalSplitViewLayout;
